import { syncRef } from '@vueuse/core';
import lodashSnakeCase from 'lodash-es/snakeCase';

import { formatSale } from './sale';
import { eventTypeStandard, getPageType, push, formatTrackingListName, type ListNameSource, attachVideoTracker } from './shared';

import { AuctionState, DirectSaleState } from '~/apiClient';
import { isAuction, isDirectSale, type SaleItemEntity } from '~/types/sale.type';

type GalleryType = 'photo' | 'video';
type GalleryContent = 'main' | 'details';
type AlertType = 'expired_revision';

export type UseTrackingSdpReturn = {
    resetTrackingReferralListName: VoidFunction;
    setTrackingReferralListName: (source: ListNameSource) => void;

    syncTrackingGlobalSale: (sale: Ref<SaleItemEntity | undefined>) => void;

    trackViewSale: (sale: SaleItemEntity) => void;
    trackGalleryView: (galleryType: GalleryType, galleryContent: GalleryContent) => void;
    trackFileDownload: (fileName: string, fileType: string) => void;
    trackSelectSale: () => void;
    trackFeeLink: () => void;

    trackAdpEmailLink: () => void;
    trackAdpConfirmButton: () => void;
    trackAdpAlertView: (alertType: AlertType) => void;
    trackAdpPlaceBid: () => void;

    trackDsdpConfirmButton: () => void;

    trackSalesContactWhatsappButton: () => void;
    trackSalesContactPhoneLink: (phone: string) => void;
    trackSalesContactOpenContactForm: () => void;
    trackSalesContactExitContactFormModal: () => void;
    trackSalesContactFocusContactFormField: (fieldName: string) => void;
    trackSalesContactContactFormValidationError: (errorType: string) => void;
    trackSalesContactSubmitContactForm: () => void;
    trackSalesContactContactFormSent: (email: string, phone: string) => void;

    attachSdpVideoTracker: (video: HTMLVideoElement) => VoidFunction;

    trackDsNegotiationStartNegotiation: () => void;
    trackNegotiationGoToNegotiation: (linkText: 'go_negotiation' | 'show_negotiation') => void;
    trackNegotiationUpdateProposal: (isBuyer: boolean) => void;
    trackNegotiationAccept: (isBuyer: boolean) => void;
    trackNegotiationSellerDecline: () => void;

    trackDsdpOrderModalConfirm: () => void;
    trackDsdpOrderModalClose: () => void;
};

const getSaleUserVisualization = (sale: SaleItemEntity): string | null => {
    const { data: session } = useAuthUtils();

    if (!isAuction(sale)) return null;

    if (!sale.negotiation || !session.value) return 'neutral';

    return sale.negotiation.buyerCompanyId === session.value.user?.companyId ? 'buyer_negotiator' : 'seller_negotiator';
};

const formatSalePageStatus = (sale: SaleItemEntity): string => {
    if (isAuction(sale)) {
        if ([AuctionState.Draft, AuctionState.ToBeScheduled, AuctionState.ToBeApproved, AuctionState.Waiting].includes(sale.state)) return 'draft';

        if ([AuctionState.Pending, AuctionState.Running, AuctionState.Expired].includes(sale.state)) return 'published';
    } else if (isDirectSale(sale)) {
        if ([DirectSaleState.Draft, DirectSaleState.ToBeScheduled, DirectSaleState.ToBeApproved, DirectSaleState.Waiting].includes(sale.state))
            return 'draft';

        if ([DirectSaleState.Pending, DirectSaleState.Open, DirectSaleState.Closed].includes(sale.state)) return 'published';
    }

    return lodashSnakeCase(sale.state);
};

export const useTrackingSdp = (): UseTrackingSdpReturn => {
    const referralListName = useState<string | null>('trackingReferralListName', () => null);
    const globalSale = useState<SaleItemEntity | undefined>('trackingGlobalSale', () => undefined);

    const setTrackingReferralListName = (source: ListNameSource): void => {
        referralListName.value = formatTrackingListName(source);
    };

    return {
        resetTrackingReferralListName(): void {
            referralListName.value = null;
        },

        setTrackingReferralListName,

        syncTrackingGlobalSale(sale): void {
            syncRef(sale, globalSale, { direction: 'ltr' });
        },

        trackViewSale(sale): void {
            push({
                event: 'view_sale',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                user_visualization: getSaleUserVisualization(sale),
                page_status: formatSalePageStatus(sale),
                ...eventTypeStandard,
            });
        },

        trackGalleryView(galleryType, galleryContent): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'gallery_view',
                gallery_type: galleryType,
                gallery_content: galleryContent,
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                user_visualization: getSaleUserVisualization(sale),
                page_status: formatSalePageStatus(sale),
                ...eventTypeStandard,
            });
        },

        trackFileDownload(fileName, fileType): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'file_download',
                file_type: fileType,
                file_name: fileName,
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackFeeLink(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'link',
                click_text: 'what_are_they',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSelectSale(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'select_sale',
                list_name: 'related_vehicles',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackAdpEmailLink(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackAdpConfirmButton(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'confirm_auction',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackAdpAlertView(alertType): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'alert_view',
                alert_type: alertType,
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackAdpPlaceBid(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'place_bid',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackDsdpConfirmButton(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'confirm_direct_sale',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactWhatsappButton(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'whatsapp',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactPhoneLink(phone): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'phone',
                click_text: phone,
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactOpenContactForm(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'contact_us',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactExitContactFormModal(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'modal_exit',
                page_type: 'contact_sales_rep',
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactFocusContactFormField(fieldName): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'field',
                click_text: lodashSnakeCase(fieldName),
                page_type: 'contact_sales_rep',
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactContactFormValidationError(errorType): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'form_error',
                error_type: errorType,
                page_type: 'contact_sales_rep',
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactSubmitContactForm(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'send_request',
                page_type: 'contact_sales_rep',
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackSalesContactContactFormSent(email, phone): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'generate_lead',
                page_type: 'contact_sales_rep',
                user_email: email,
                user_phone: phone || null,
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        attachSdpVideoTracker(video): VoidFunction {
            const filename = video.src.split('/').pop();
            const videoName = filename ? lodashSnakeCase(filename.split('.').slice(0, -1).join('.')) : null;
            const sale = globalSale.value;

            return attachVideoTracker(video, {
                onPlay() {
                    if (!sale) return;

                    push({
                        event: 'video_start',
                        page_type: getPageType(),
                        video_name: videoName,
                        sales: [formatSale(sale, 0, referralListName.value)],
                        ...eventTypeStandard,
                    });
                },
                onProgress(percent) {
                    if (!sale) return;

                    push({
                        event: 'video_progress',
                        page_type: getPageType(),
                        video_name: videoName,
                        video_percent: percent,
                        sales: [formatSale(sale, 0, referralListName.value)],
                        ...eventTypeStandard,
                    });
                },
                onEnded() {
                    if (!sale) return;

                    push({
                        event: 'video_complete',
                        page_type: getPageType(),
                        video_name: videoName,
                        sales: [formatSale(sale, 0, referralListName.value)],
                        ...eventTypeStandard,
                    });
                },
            });
        },

        trackDsNegotiationStartNegotiation(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'make_offer',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackNegotiationGoToNegotiation(clickText): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: clickText,
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackNegotiationUpdateProposal(isBuyer): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: isBuyer ? 'change_offer' : 'change_ask',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackNegotiationAccept(isBuyer): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: isBuyer ? 'accept_ask' : 'accept_offer',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackNegotiationSellerDecline(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'end_negotiation',
                page_type: getPageType(),
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackDsdpOrderModalConfirm(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'buy_now',
                page_type: 'place_order',
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },

        trackDsdpOrderModalClose(): void {
            const sale = globalSale.value;
            if (!sale) return;

            push({
                event: 'click',
                click_element: 'button',
                click_text: 'close',
                page_type: 'place_order',
                sales: [formatSale(sale, 0, referralListName.value)],
                ...eventTypeStandard,
            });
        },
    };
};
